// useCustomScrollbar.js — Reusable hook for the kp-scrollbox custom scrollbar.
// Place at: src/hooks/useCustomScrollbar.js
// Used by both TnCScreen and PrivacyPolicy.

import { useRef, useEffect, useCallback } from 'react'

export function useCustomScrollbar() {
  const contentRef = useRef(null)
  const thumbRef   = useRef(null)
  const trackRef   = useRef(null)
  const isDragging = useRef(false)
  const dragStartY = useRef(0)
  const dragStartScrollTop = useRef(0)
  const activePointerId = useRef(null)

  // ── Sync thumb size & position ────────────────────────────────────────────
  const syncThumb = useCallback(() => {
    const content = contentRef.current
    const thumb   = thumbRef.current
    const track   = trackRef.current
    if (!content || !thumb || !track) return

    const trackH     = track.clientHeight
    const contentH   = content.scrollHeight
    const visibleH   = content.clientHeight
    const scrollable = contentH - visibleH
    if (trackH <= 0 || contentH <= 0 || visibleH <= 0) return

    const thumbH = Math.max(40, (visibleH / contentH) * trackH)
    thumb.style.height = `${thumbH}px`

    const maxThumbTop = trackH - thumbH
    const thumbTop = scrollable > 0
      ? (content.scrollTop / scrollable) * maxThumbTop
      : 0
    thumb.style.top = `${thumbTop}px`
    thumb.style.display = scrollable > 0 ? 'block' : 'none'
  }, [])

  // ── Scroll listener ───────────────────────────────────────────────────────
  useEffect(() => {
    const content = contentRef.current
    const track = trackRef.current
    if (!content || !track) return

    syncThumb()
    content.addEventListener('scroll', syncThumb, { passive: true })
    window.addEventListener('resize', syncThumb)

    const resizeObserver = typeof ResizeObserver !== 'undefined'
      ? new ResizeObserver(() => syncThumb())
      : null

    resizeObserver?.observe(content)
    resizeObserver?.observe(track)

    return () => {
      content.removeEventListener('scroll', syncThumb)
      window.removeEventListener('resize', syncThumb)
      resizeObserver?.disconnect()
    }
  }, [syncThumb])

  // ── Thumb drag start ──────────────────────────────────────────────────────
  const onThumbPointerDown = useCallback((e) => {
    e.preventDefault()
    isDragging.current = true
    activePointerId.current = e.pointerId ?? null
    dragStartY.current = e.clientY
    dragStartScrollTop.current = contentRef.current?.scrollTop || 0
    e.currentTarget?.setPointerCapture?.(e.pointerId)
    document.body.style.userSelect = 'none'
  }, [])

  // ── Global move & up ──────────────────────────────────────────────────────
  useEffect(() => {
    const onMove = (e) => {
      if (!isDragging.current) return
      if (activePointerId.current !== null && e.pointerId !== activePointerId.current) return
      const content = contentRef.current
      const thumb   = thumbRef.current
      const track   = trackRef.current
      if (!content || !thumb || !track) return
      e.preventDefault()

      const clientY     = e.clientY
      const deltaY      = clientY - dragStartY.current
      const trackH      = track.clientHeight
      const thumbH      = thumb.clientHeight
      const maxThumbTop = trackH - thumbH
      const scrollable  = content.scrollHeight - content.clientHeight
      if (maxThumbTop <= 0 || scrollable <= 0) return
      content.scrollTop = Math.min(
        scrollable,
        Math.max(0, dragStartScrollTop.current + (deltaY / maxThumbTop) * scrollable)
      )
    }

    const onUp = () => {
      isDragging.current = false
      activePointerId.current = null
      document.body.style.userSelect = ''
    }

    window.addEventListener('pointermove', onMove, { passive: false })
    window.addEventListener('pointerup', onUp)
    window.addEventListener('pointercancel', onUp)
    return () => {
      window.removeEventListener('pointermove', onMove)
      window.removeEventListener('pointerup', onUp)
      window.removeEventListener('pointercancel', onUp)
    }
  }, [])

  // ── Click track to jump ───────────────────────────────────────────────────
  const onTrackClick = useCallback((e) => {
    if (e.target === thumbRef.current) return
    const content = contentRef.current
    const track   = trackRef.current
    const thumb   = thumbRef.current
    if (!content || !track || !thumb) return

    const trackRect   = track.getBoundingClientRect()
    const clickY      = e.clientY - trackRect.top
    const thumbH      = thumb.clientHeight
    const trackH      = track.clientHeight
    const maxThumbTop = trackH - thumbH
    const scrollable  = content.scrollHeight - content.clientHeight
    if (maxThumbTop <= 0 || scrollable <= 0) return
    content.scrollTop = Math.min(
      scrollable,
      Math.max(0, ((clickY - thumbH / 2) / maxThumbTop) * scrollable)
    )
  }, [])

  return { contentRef, thumbRef, trackRef, onThumbPointerDown, onTrackClick, syncThumb }
}
